/**
 * 3D Room Visualizer — Material Factory
 *
 * Creates color-accurate Three.js materials for product visualization.
 * Addresses the white-looks-gray problem with proper material configuration.
 */

import * as THREE from 'three';

// Product color hex values
const COLOR_WHITE = 0xffffff;
const COLOR_GRAY = 0xd0d3d4; // Pantone 427 CP

const COLOR_WHITE_HEX = '#FFFFFF';
const COLOR_GRAY_HEX = '#D0D3D4';

/**
 * Get hex color value for a product color name.
 */
export function getProductColorHex(color: 'White' | 'Gray'): string {
  return color === 'White' ? COLOR_WHITE_HEX : COLOR_GRAY_HEX;
}

/**
 * Get Three.js color integer for a product color name.
 */
export function getProductColor(color: 'White' | 'Gray'): number {
  return color === 'White' ? COLOR_WHITE : COLOR_GRAY;
}

/**
 * Create a material for board/panel surfaces.
 * Uses a blend of diffuse + emissive: emissive keeps whites bright and
 * lighting-stable, while diffuse allows shadows to modulate the surface.
 */
export function createBoardMaterial(
  color: 'White' | 'Gray',
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = false, opacity = 1.0 } = options;
  const colorHex = getProductColor(color);

  return new THREE.MeshStandardMaterial({
    color: colorHex,             // Diffuse component — receives shadows
    roughness: 0.3,
    metalness: 0.1,
    emissive: colorHex,          // Emissive floor — keeps color bright in low light
    emissiveIntensity: 0.4,
    side: THREE.DoubleSide,
    transparent: transparent || opacity < 1.0,
    opacity,
    depthWrite: opacity > 0.9,
  });
}

/**
 * Create a material for wall shell planes (backing behind boards).
 * Dark color so board gaps appear as visible seam lines.
 */
export function createWallShellMaterial(
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = false, opacity = 1.0 } = options;

  return new THREE.MeshStandardMaterial({
    color: 0x444444,
    roughness: 0.9,
    metalness: 0.0,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: Math.min(opacity, 0.6),
    depthWrite: false,
  });
}

/**
 * Create a legacy wall material (solid color plane, used when showBoards=false).
 * Improved from original to render white accurately.
 */
export function createLegacyWallMaterial(
  color: 'White' | 'Gray',
  highlighted: boolean,
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = true, opacity = 0.4 } = options;

  const materialColor = highlighted
    ? getProductColor(color)
    : 0xe5e7eb;

  return new THREE.MeshStandardMaterial({
    color: materialColor,
    side: THREE.DoubleSide,
    transparent,
    opacity,
    emissive: (highlighted && color === 'White') ? 0x333333 : 0x000000,
    emissiveIntensity: (highlighted && color === 'White') ? 0.2 : 0,
    roughness: 0.5,
    metalness: 0.0,
  });
}

/**
 * Create ceiling material (legacy mode).
 */
export function createLegacyCeilingMaterial(
  color: 'White' | 'Gray',
  highlighted: boolean,
  options: {
    transparent?: boolean;
    opacity?: number;
  } = {}
): THREE.MeshStandardMaterial {
  const { transparent = true, opacity = 0.3 } = options;

  const materialColor = highlighted
    ? getProductColor(color)
    : 0xf3f4f6;

  return new THREE.MeshStandardMaterial({
    color: materialColor,
    side: THREE.DoubleSide,
    transparent,
    opacity,
    emissive: (highlighted && color === 'White') ? 0x333333 : 0x000000,
    emissiveIntensity: (highlighted && color === 'White') ? 0.2 : 0,
    roughness: 0.5,
    metalness: 0.0,
  });
}

/**
 * Create floor material.
 */
/**
 * Generate a procedural concrete/orange-peel texture via canvas.
 * Creates a subtle mottled pattern with fine speckle noise.
 */
function createConcreteTexture(): THREE.CanvasTexture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Base warm concrete color
  ctx.fillStyle = '#d4ccc0';
  ctx.fillRect(0, 0, size, size);

  // Larger mottled patches (orange peel effect)
  for (let i = 0; i < 800; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 2 + Math.random() * 6;
    const shade = 180 + Math.floor(Math.random() * 40);
    const alpha = 0.08 + Math.random() * 0.12;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${shade}, ${shade - 8}, ${shade - 16}, ${alpha})`;
    ctx.fill();
  }

  // Fine speckle noise
  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 14;
    data[i] = Math.min(255, Math.max(0, data[i] + noise));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
  }
  ctx.putImageData(imageData, 0, 0);

  // Subtle darker streaks
  for (let i = 0; i < 15; i++) {
    const x1 = Math.random() * size;
    const y1 = Math.random() * size;
    const x2 = x1 + (Math.random() - 0.5) * 200;
    const y2 = y1 + (Math.random() - 0.5) * 200;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = `rgba(140, 130, 120, ${0.04 + Math.random() * 0.06})`;
    ctx.lineWidth = 1 + Math.random() * 3;
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(4, 4);
  return texture;
}

/**
 * Create floor material. Loads dirty concrete bitmap by default.
 * The texture loads asynchronously; material starts with the procedural
 * fallback and swaps to the bitmap once loaded.
 */
export function createFloorMaterial(
  highlighted: boolean,
  onTextureLoaded?: () => void
): THREE.MeshStandardMaterial {
  // Start with procedural texture as immediate fallback
  const procTexture = createConcreteTexture();

  const mat = new THREE.MeshStandardMaterial({
    map: procTexture,
    color: highlighted ? 0xd4c5a0 : 0xffffff,
    side: THREE.DoubleSide,
    transparent: false,
    opacity: 1.0,
    roughness: 1.0,
    metalness: 0.0,
  });

  // Load the default bitmap texture asynchronously
  const loader = new THREE.TextureLoader();
  loader.load('/textures/floor-concrete-dirty.jpg', (texture) => {
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(3, 3);
    texture.colorSpace = THREE.SRGBColorSpace;
    procTexture.dispose();
    mat.map = texture;
    mat.needsUpdate = true;
    if (onTextureLoaded) onTextureLoaded();
  });

  return mat;
}
